// libs
import { styled } from '@nykaa/ui-components';

// Base components
import BaseImage from 'shared/components/ImageV2';
import RippleIt from 'shared/components/RippleIt';
import BaseOverlaySheet from 'shared/components/OverlaySheet';
import { BREAKPOINTS, FOCUS_VISIBILITY_CSS } from 'shared/styles/base';

// mixins
import { resetButton } from 'shared/styles/mixin';

export const Article = styled.article`
  word-break: break-word;
`;

export const Title = styled.h4`
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: ${({ theme }) => `0 0 ${theme.spacing.spacing20}`};
  ${({ theme }) => theme.typography.buttonMedium}

  &::first-letter {
    text-transform: uppercase;
  }
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin-bottom: ${({ theme }) => `${theme.spacing.spacing10}`};
    ${({ theme }) => theme.typography.titleSmall}
  }
`;

export const Details = styled.p`
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: ${({ theme }) => `0 0 ${theme.spacing.spacing60}`};
  ${({ theme }) => theme.typography.bodySmall};

  &::first-letter {
    text-transform: uppercase;
  }
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    ${({ theme }) => theme.typography.bodyMedium};
  }
`;

export const Marker = styled.div`
  display: flex;
  position: relative;
  height: 100%;
  align-items: center;
  .date {
    display: flex;
  }
`;

export const CheckMarker = styled.span`
  display: inline-block;
  height: 14px;
  width: 14px;
  border-radius: ${({ theme }) => theme.borders.radiusFull};
  background-color: ${({ theme }) => theme.colors.textDisabled};
  position: relative;
  margin-right: ${({ theme }) => theme.spacing.spacing40};

  &:after {
    content: '';
    position: absolute;
    right: 2px;
    top: -1px;
    height: 9px;
    width: 3px;
    background-color: transparent;
    border: 1px solid #fff;
    border-color: transparent #fff #fff transparent;
    transform: rotate(45deg);
  }
`;

export const User = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.spacing60};

  .info {
    display: block;
    width: calc(100% - 56px);
    color: ${({ theme }) => theme.colors.textPrimary};
    .name {
      ${({ theme }) => theme.typography.subTitleMedium};
      color: ${({ theme }) => theme.colors.textPrimary};
      text-transform: capitalize;
      margin-right: ${({ theme }) => theme.spacing.spacing50};
      @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
        ${({ theme }) => theme.typography.subTitleLarge};
        margin-right: ${({ theme }) => theme.spacing.spacing60};
      }
    }

    .verified {
      ${({ theme }) => theme.typography.bodySmall};
      color: ${({ theme }) => theme.colors.textSecondary};
      margin-left: ${({ theme }) => theme.spacing.spacing10};
      @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
        ${({ theme }) => theme.typography.bodyMedium};
      }
    }
    .reviewedOn {
      ${({ theme }) => theme.typography.bodySmall};
      color: ${({ theme }) => theme.colors.textSecondary};
    }
  }
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    margin-bottom: ${({ theme }) => theme.spacing.spacing80};
  }
`;

export const Row = styled.div`
  flex-direction: column;
  display: flex;
`;

export const ProductInfo = styled(Row)<{ shrink?: boolean; isPaddingTop: boolean }>`
  display: flex;
  flex-direction: row;
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-wrap: wrap;
  align-items: center;

  ${({ isPaddingTop, theme }) => isPaddingTop && `margin-top: ${theme.spacing.spacing10};`};
  .color {
    margin-right: ${({ theme }) => theme.spacing.spacing20};
  }
  margin-bottom: ${({ theme }) => theme.spacing.spacing60};
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    ${({ theme }) => theme.typography.subTitleMedium};
    margin-bottom: ${({ theme }) => theme.spacing.spacing80};
    ${({ isPaddingTop, theme }) => isPaddingTop && `margin-top: ${theme.spacing.spacing40};`};
  }
`;

export const Tag = styled.span<{ light?: boolean; addGap?: boolean }>`
  display: inline-block;
  ${({ theme }) => theme.typography.subTitleSmall};
  ${({ addGap, theme }) => addGap && `margin-right: ${theme.spacing.spacing40}`};
  color: ${({ light, theme }) => (light ? theme.colors.textSecondary : theme.colors.textPrimary)};
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    ${({ theme, light }) =>
      light ? theme.typography.bodyMedium : theme.typography.subTitleMedium};
  }
`;

export const RatingRow = styled.div`
  margin: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing.spacing60};
  height: 16px;
  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    height: 24px;
  }

  .star-rating {
    height: 16px;
    display: flex;
    .active {
      margin-right: ${({ theme }) => theme.spacing.spacing10};
    }

    .in-active {
      margin-right: ${({ theme }) => theme.spacing.spacing10};
    }
    @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
      height: 24px;
    }
  }

  .date {
    ${({ theme }) => theme.typography.bodySmall};
    color: ${({ theme }) => theme.colors.textSecondary};
    display: flex;
    @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
      ${({ theme }) => theme.typography.bodyMedium};
    }
  }
`;

export const ImageList = styled.div`
  display: flex;
  overflow-y: hidden;
  overflow-x: auto;
  width: 100%;
`;

export const UserImageItemWrapper = styled.button`
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borders.radius10};
  border: 1px solid ${({ theme }) => theme.colors.textDecorative};
  height: 96px;
  width: 72px;
  margin-right: ${({ theme }) => theme.spacing.spacing40};
  padding: 0;
  background: none;
  cursor: pointer;
  ${FOCUS_VISIBILITY_CSS}
`;

export const ImageItem = styled(BaseImage)`
  object-fit: cover;
  min-height: 96px;
  cursor: pointer;
`;

export const OverlaySheet = styled(BaseOverlaySheet)`
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: #fff;
`;

export const BackIcon = styled(RippleIt)`
  background-color: #f7f7f7;
  border-radius: ${({ theme }) => theme.borders.radiusFull};
  display: inline-flex;
  height: 32px;
  width: 32px;
  left: 8px;
  top: 8px;
  position: fixed;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  z-index: 1;
  cursor: pointer;

  ${FOCUS_VISIBILITY_CSS}
`;

export const ZoomContainer = styled.div`
  background: #fff;
`;

export const CarouselWrapper = styled.div`
  display: flex;
  height: 100vh;
  align-items: center;

  .image-wrapper {
    ${resetButton}
  }
`;

export const Clicker = styled.span`
  position: absolute;
  top: 12px;
  height: 4px;
  border-radius: ${({ theme }) => theme.borders.radius10};
  background-color: ${({ theme }) => theme.colors.textOutline};
  width: 32px;
  left: 50%;
  transform: translateX(-16px);
`;

export const ReviewWrapper = styled.div<{ showDetails: boolean }>`
  position: absolute;
  z-index: 1;
  bottom: 0;
  padding: ${({ theme }) => `${theme.spacing.spacing160} ${theme.spacing.spacing80}`};
  width: 100%;
  border-radius: ${({ theme }) => `${theme.borders.radius40} ${theme.borders.radius40} 0 0`};
  box-shadow: ${({ theme }) => theme.elevations.shadow100};
  background-color: transparent;
  transition: transform 0.3s ease-in-out;
  transform: translate(0, ${({ showDetails }) => (showDetails ? '0' : '50%')});
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: -2px;
  }

  &:before {
    content: '';
    transition: all 0.2s ease-in;
    border-radius: ${({ theme }) => `${theme.borders.radius40} ${theme.borders.radius40} 0 0`};
    background: rgba(255, 255, 255, ${({ showDetails }) => (showDetails ? '1' : '0.8')});
    position: absolute;
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    z-index: -1;
  }
`;

export const Dot = styled.div`
  height: 4px;
  width: 4px;
  ${({ theme }) =>
    `border-radius: ${theme.borders.radius40}; 
  margin: 0 ${theme.spacing.spacing40}; background-color: ${theme.colors.textDisabled}`};
`;

export const RatingCountWrapper = styled.div`
  display: flex;
  min-width: 44px;
  ${({ theme }) =>
    `border-radius: ${theme.borders.radius10}; 
  border: 1px solid ${theme.colors.textDecorative};
  margin: 0 0 0 ${theme.spacing.spacing80};
  padding: ${theme.spacing.spacing20} ${theme.spacing.spacing40};
  background-color: ${theme.colors.surface10}`};
  align-items: center;
  .rating-count {
    align-items: center;
    display: flex;
    ${({ theme }) => theme.typography.buttonMedium}
  }
  .star-wrapper {
    margin: 0 0 0 ${({ theme }) => theme.spacing.spacing20};
    align-items: center;
    display: flex;
    height: 100%;
  }
`;
